import { useState } from 'react'
import { Lock } from 'lucide-react'

import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import { formatServiceStatus } from '@/help/serviceStatus'
import { getServiceStatusMeta } from '@/lib/serviceStatusMeta'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface LockedStatusCardProps {
  resourceLabel: string
  status: 'Invoiced' | 'Cancelled'
  actionLabel?: string
  actionTitle?: string
  actionDescription?: string
  lockedMessage?: string
  reasonLabel?: string
  reasonPlaceholder?: string
  confirmLabel?: string
  cancelLabel?: string
  onAction?: (reason?: string) => void | Promise<void>
  requireReason?: boolean
}

export function LockedStatusCard({
  resourceLabel,
  status,
  actionLabel,
  actionTitle,
  actionDescription,
  lockedMessage: lockedMessageOverride,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  cancelLabel,
  onAction,
  requireReason = false,
}: LockedStatusCardProps) {
  const [isPending, setIsPending] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [reason, setReason] = useState('')

  const handleConfirmAction = async () => {
    if (!onAction || isPending) return
    const normalizedReason = reason.trim()
    if (requireReason && !normalizedReason) return

    setIsPending(true)
    try {
      await Promise.resolve(onAction(requireReason ? normalizedReason : undefined))
      setIsDialogOpen(false)
      setReason('')
    } finally {
      setIsPending(false)
    }
  }

  const lockedLabel = status === 'Invoiced' ? 'Invoiced' : 'Cancelled'
  const lockedMessage =
    lockedMessageOverride ??
    (status === 'Invoiced'
      ? `This ${resourceLabel} is locked. No changes can be made while it is Invoiced. Move it to Review to unlock.`
      : `This ${resourceLabel} is locked. No changes can be made while it is Cancelled. Move it back to Booked to unlock.`)
  const defaultActionLabel = status === 'Invoiced' ? 'Move to Review' : 'Move to Booked'
  const resolvedActionLabel = actionLabel ?? defaultActionLabel

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <h2 className="text-sm font-semibold text-slate-800">Status</h2>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${getServiceStatusMeta(status).dotClass}`}
          />
          <span
            className={`text-[13px] font-semibold ${getServiceStatusMeta(status).textClass}`}
          >
            {formatServiceStatus(status)}
          </span>
          <Lock className="h-3.5 w-3.5 text-slate-500" aria-label="Locked" />
        </div>
        {onAction ? (
          <button
            type="button"
            className="inline-flex h-[30px] cursor-pointer items-center gap-2 rounded-[7px] border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isPending}
            onClick={() => {
              if (requireReason) {
                setReason('')
                setIsDialogOpen(true)
                return
              }
              void handleConfirmAction()
            }}
          >
            {isPending ? <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" /> : null}
            {resolvedActionLabel}
          </button>
        ) : null}
      </div>
      <p className="text-xs text-slate-500">{lockedMessage}</p>
      {onAction && requireReason ? (
        <Dialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open)
                if (!open) setReason('')
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{actionTitle ?? `Move this ${resourceLabel} to Review?`}</DialogTitle>
                  <DialogDescription>
                    {actionDescription ?? `Add a short reason so the office team knows why this is being moved back to Review.`}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 pt-2">
                  <FormCapsLabel>{reasonLabel ?? 'Reason (required)'}</FormCapsLabel>
                  {/* Keyed on the dialog session so reopening starts empty —
                      tiptap owns its document, so clearing `reason` alone
                      would leave the previous text on screen. */}
                  <RichMarkdownEditor
                    key={String(isDialogOpen)}
                    initialValue=""
                    onChange={setReason}
                    placeholder={reasonPlaceholder ?? 'Required. E.g. invoice dispute, missing details, customer query…'}
                    ariaLabel={reasonLabel ?? 'Reason'}
                  />
                </div>
                <DialogFooter>
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    disabled={isPending}
                    onClick={() => {
                      setIsDialogOpen(false)
                      setReason('')
                    }}
                  >
                    {cancelLabel ?? `Keep ${lockedLabel}`}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!reason.trim() || isPending}
                    onClick={handleConfirmAction}
                  >
                    {isPending ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : null}
                    {confirmLabel ?? resolvedActionLabel}
                  </button>
                </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </section>
  )
}
